// Hardcoded rather than pulled from an env var — Hostinger's static/Vite
// deployment wasn't reliably injecting VITE_API_URL into the build, so
// this removes that point of failure entirely. Not a secret (it's a
// public API URL), so hardcoding it is fine.
export const API_ORIGIN = 'https://api.stsgp.com';
const BASE = `${API_ORIGIN}/api`;

function authHeaders() {
  const token = localStorage.getItem('loadline_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Stored login session (12h JWT) — cleared here when the server rejects it,
// so the app never stays on a screen where every request fails with
// "Invalid or expired token". App.jsx listens for SESSION_EXPIRED_EVENT and
// returns to the login screen.
export const SESSION_EXPIRED_EVENT = 'loadline:session-expired';
export function clearStoredSession() {
  localStorage.removeItem('loadline_token');
  localStorage.removeItem('loadline_user');
  localStorage.removeItem('loadline_impersonating');
}

// exp from the JWT payload, read without verifying (the server verifies) —
// just so an obviously-expired stored session is dropped on load.
export function storedTokenExpired(token = localStorage.getItem('loadline_token')) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch {
    return false; // not a JWT we can read — let the server decide
  }
}

// 401 on a request that carried a login token (not /auth/* — a wrong
// password on /auth/login is also a 401) = the session is no longer valid.
function handleUnauthorized(path, sentToken) {
  if (!sentToken || path.startsWith('/auth/')) return;
  clearStoredSession();
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

export async function api(path, options = {}) {
  // Callers are inconsistent about whether they pass a raw object (the
  // documented contract) or a pre-stringified JSON string for `body` —
  // stringifying an already-stringified body produces a JSON string
  // instead of a JSON object at the top level of the request, which
  // Express's body parser (strict mode, the default) rejects outright.
  // Handling both shapes here means every existing call site is fixed
  // at once instead of having to track down and edit each one.
  const body = options.body
    ? (typeof options.body === 'string' ? options.body : JSON.stringify(options.body))
    : undefined;
  const auth = authHeaders();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...auth,
      ...(options.headers || {})
    },
    body
  });
  if (res.status === 401) handleUnauthorized(path, !!auth.Authorization);
  if (!res.ok) throw new Error((await res.json()).error || 'Request failed');
  return res.json();
}

// Separate from api() because a file upload needs multipart/form-data with
// a browser-generated boundary — setting Content-Type manually (like api()
// does for JSON) would break that. Still sends the same auth header.
export async function uploadFile(path, file) {
  const formData = new FormData();
  formData.append('file', file);
  const auth = authHeaders();
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { ...auth },
    body: formData
  });
  if (res.status === 401) handleUnauthorized(path, !!auth.Authorization);
  if (!res.ok) throw new Error((await res.json()).error || 'Upload failed');
  return res.json();
}
